import React, { useState, useEffect } from 'react';
import config from './config';
import PriorArtPage from './PriorArtPage';
import ReactMarkdown from 'react-markdown';
import './App.css';
import CitationAnalysisPage from './CitationAnalysisPage';
import CitationNetworkPage from './CitationNetworkPage';
import PatentDecoderPage from './PatentDecoderPage';
import PatentabilityCheckPage from './PatentabilityCheckPage';
import FTOAssessmentPage from './FTOAssessmentPage';
import TechLandscapePage from './TechLandscapePage';
import InventionAssessmentPage from './InventionAssessmentPage';
import PriorArtDeepDivePage from './PriorArtDeepDivePage';
import FTOProPage from './FTOProPage';
import CompetitiveIntelligencePage from './CompetitiveIntelligencePage';
import Tour from './Tour';
import WorkspacesContent from './components/WorkspacesContent';
import DashboardPage from './components/DashboardPage';
import PricingPage from './components/PricingPage';
import LoginPage from './components/LoginPage';
import SavedWorkspaceComparisons from './components/SavedWorkspaceComparisons';
import RegisterPage from './components/RegisterPage';
import FAQPage from './components/FAQPage';
import SavedPatentsPage from './components/SavedPatentsPage';
import SavedComparisonsPage from './components/SavedComparisonsPage';
import SavedSearchesPage from './components/SavedSearchesPage';
import ComparisonWorkspace from './components/ComparisonWorkspace';
import CompanyIntelPage from './components/CompanyIntelPage';
import SavedPriorArtSearchesPage from './components/SavedPriorArtSearchesPage';
import AdminPage from './components/AdminPage';
import SavedInventionAssessmentsPage from './components/SavedInventionAssessmentsPage';
import SavedIndividualReportsPage from './components/SavedIndividualReportsPage';
import ComparePanel from './components/ComparePanel';
import CompareTray from './components/CompareTray';
import LoginPromptModal from './components/LoginPromptModal';
import CompanyDisambiguationModal from './components/CompanyDisambiguationModal';
import ShareToWorkspaceModal from './components/ShareToWorkspaceModal';import SidebarMenu from './components/SidebarMenu';
import PatentCard from './components/PatentCard';
import LandscapeInsightPanel from './components/LandscapeInsightPanel';
import TopAssigneesPanel from './components/TopAssigneesPanel';
import ResultsHeader from './components/ResultsHeader';
import TransparencyPanel from './components/TransparencyPanel';
import Header from './components/Header';
import AdvancedToolsPathOptions from './components/AdvancedToolsPathOptions';
import SearchForm from './components/SearchForm';
import SearchActionsBar from './components/SearchActionsBar';
import ResultsSortBar from './components/ResultsSortBar';
import EmptyResultsMessage from './components/EmptyResultsMessage';
import AnalysisToolsSection from './components/AnalysisToolsSection';
import AdvancedToolsToggle from './components/AdvancedToolsToggle';
import { CPC_DEFINITIONS } from './constants';

// CPC Classification Definitions for PatTrak

// Workspaces Component
// Component moved to components/WorkspacesContent.js

function App() {

const CPC_SECTIONS = {
  'A': 'Human Necessities',
  'B': 'Performing Operations; Transporting',
  'C': 'Chemistry; Metallurgy',
  'D': 'Textiles; Paper',
  'E': 'Fixed Constructions',
  'F': 'Mechanical Engineering; Lighting; Heating; Weapons',
  'G': 'Physics',
  'H': 'Electricity',
  'Y': 'Cross-sectional Technologies'
};


  const [page, setPage] = useState('search'); // search, login, register, admin, faq, pricing, prior-art
  const [showTour, setShowTour] = useState(false);
  
  // Tour steps for main search page
  const mainTourSteps = [
    {
      target: '[data-tour="hamburger-menu"]',
      title: 'Welcome to PatTrak! ??',
      content: 'Let\'s take a quick tour. This menu gives you access to your account, saved searches, workspaces, pricing, and FAQ.',
      placement: 'bottom'
    },
    {
      target: '[data-tour="database-selector"]',
      title: 'Choose Your Database',
      content: 'Search our Local Database of 1.45 million USPTO patents, or switch to Espacenet for real-time patent office searches.',
      placement: 'bottom'
    },
    {
      target: '[data-tour="patent-status"]',
      title: 'Filter by Patent Status',
      content: 'Filter results by Live patents (still in force), Lapsed patents (expired), or Both.',
      placement: 'bottom'
    },
    {
      target: '[data-tour="search-types"]',
      title: 'Search Types',
      content: 'Choose how to search: AI understands natural language, or search by Patent #, Company name, or Inventor.',
      placement: 'bottom'
    },
    {
      target: '[data-tour="unlimited-message"]',
      title: 'Free Unlimited Searches',
      content: 'Patent #, Company, and Inventor searches are completely free and unlimited - no login required!',
      placement: 'bottom'
    },
    {
      target: '[data-tour="more-tools"]',
      title: 'Advanced Analysis Tools',
      content: 'Click here to access powerful tools: Search Pro, Company Intelligence, Citation Analysis, Network visualization, and Prior Art reports.',
      placement: 'bottom'
    },
    {
      target: '[data-tour="search-box"]',
      title: 'Start Searching',
      content: 'Enter your search here - try a technology like "solar panels" or a company name like "Tesla". Press Enter or click Search to see results.',
      placement: 'top'
    },
    {
      target: '[data-tour="help-button"]',
      title: 'Need Help?',
      content: 'Click the ? button anytime to replay this tour or get help. Happy searching!',
      placement: 'bottom'
    }
  ];

  // Check if user should see tour on first visit
  useEffect(() => {
    const tourCompleted = localStorage.getItem('tour_completed_main');
    if (!tourCompleted && page === 'search') {
      // Small delay to let page render
      const timer = setTimeout(() => setShowTour(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);
  const [priorArtResults, setPriorArtResults] = useState(null);
  const [priorArtLoading, setPriorArtLoading] = useState(false);
  const [priorArtProgress, setPriorArtProgress] = useState({ progress: 0, stage: '' });
  const [priorArtForm, setPriorArtForm] = useState({
    invention_title: '',
    invention_description: '',
    distilled_claim: '',
    priority_date: '',
    jurisdiction: 'US',
    report_type: 'attorney',
    key_features: ['', '', ''],
    known_competitors: ''
  });
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('ai'); // 'ai', 'patent', 'assignee', 'inventor'
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showSearchTips, setShowSearchTips] = useState(false);
  const [landscapeData, setLandscapeData] = useState(null);
  const [landscapeLoading, setLandscapeLoading] = useState(false);
  const [showLandscapeCharts, setShowLandscapeCharts] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [savedAiAnalysis, setSavedAiAnalysis] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [patentToShare, setPatentToShare] = useState(null);
  const [userWorkspaces, setUserWorkspaces] = useState([]);
  const [shareNote, setShareNote] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [showComparePanel, setShowComparePanel] = useState(false);
  const [loginPromptMessage, setLoginPromptMessage] = useState('');
  const [showMoreTools, setShowMoreTools] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [grantedCount, setGrantedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [source, setSource] = useState('local');
  const [patentStatus, setPatentStatus] = useState('both');
  const [loading, setLoading] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [user, setUser] = useState(null);
  const [userUsage, setUserUsage] = useState(null);
  const [freeMode, setFreeMode] = useState(false);
  const [cacheStats, setCacheStats] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [patentSummaries, setPatentSummaries] = useState({}); // NEW: Store summaries by patent ID
  const [analysisLoading, setAnalysisLoading] = useState({});
  const [transparency, setTransparency] = useState(null);
  const [topAssignees, setTopAssignees] = useState([]);
  const [showTransparency, setShowTransparency] = useState(true);
  const [savedPatents, setSavedPatents] = useState(new Set());
  const [savedInventionAssessments, setSavedInventionAssessments] = useState([]);
  const [savedIndividualReports, setSavedIndividualReports] = useState([]);
  const [savingPatent, setSavingPatent] = useState(null);
  const [viewSavedPatents, setViewSavedPatents] = useState([]);
  const [savedPriorArtSearches, setSavedPriorArtSearches] = useState([]);
  const [appSearchQuery, setAppSearchQuery] = useState('');
  const [appSearchAssignee, setAppSearchAssignee] = useState('');
  const [appSearchCpc, setAppSearchCpc] = useState('');
  const [appSearchResults, setAppSearchResults] = useState(null);
  const [expandedClaims, setExpandedClaims] = useState(null);
  const [claimsData, setClaimsData] = useState({});
  const [claimsLoading, setClaimsLoading] = useState(null);
  const [expandedSummary, setExpandedSummary] = useState(null);
  const [summaryData, setSummaryData] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(null);
  const [expandedPct, setExpandedPct] = useState(null);
  const [pctData, setPctData] = useState({});
  const [pctLoading, setPctLoading] = useState(null);
  const [expandedDesc, setExpandedDesc] = useState(null);
  const [descData, setDescData] = useState({});
  const [descLoading, setDescLoading] = useState(null);
  const [appSearchLoading, setAppSearchLoading] = useState(false);
  const [companySearchName, setCompanySearchName] = useState('');
  const [companyIntel, setCompanyIntel] = useState(null);
  const [citationCounts, setCitationCounts] = useState({});
  const [expandedCitations, setExpandedCitations] = useState(null);
  const [citationDetails, setCitationDetails] = useState({ forward: [], backward: [] });
  const [citationDetailsLoading, setCitationDetailsLoading] = useState(false);
  const [expandedFamily, setExpandedFamily] = useState(null);
  const [familyData, setFamilyData] = useState({ foreign_priorities: [], family_members: [] });
  const [familyLoading, setFamilyLoading] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
const [statusFilter, setStatusFilter] = useState('all');
  const [showCompanyDisambiguation, setShowCompanyDisambiguation] = useState(false);
  const [companyMatches, setCompanyMatches] = useState([]);
  const [pendingCompanySearch, setPendingCompanySearch] = useState('');

  // CPC Class Descriptions for tooltips
  const cpcDescriptions = {
    'A': 'Human Necessities',
    'A01': 'Agriculture; Forestry; Animal Husbandry',
    'A21': 'Baking; Edible Doughs',
    'A22': 'Butchering; Meat Treatment',
    'A23': 'Foods or Foodstuffs',
    'A24': 'Tobacco; Cigars; Cigarettes',
    'A41': 'Wearing Apparel',
    'A42': 'Headwear',
    'A43': 'Footwear',
    'A44': 'Haberdashery; Jewellery',
    'A45': 'Hand or Travel Articles',
    'A46': 'Brushware',
    'A47': 'Furniture; Domestic Articles',
    'A61': 'Medical or Veterinary Science; Hygiene',
    'A62': 'Life-Saving; Fire-Fighting',
    'A63': 'Sports; Games; Amusements',
    'B': 'Performing Operations; Transporting',
    'B01': 'Physical or Chemical Processes',
    'B02': 'Crushing, Grinding, Pulverising',
    'B03': 'Separation of Solids',
    'B04': 'Centrifugal Apparatus',
    'B05': 'Spraying or Atomising',
    'B06': 'Generating or Transmitting Mechanical Vibrations',
    'B07': 'Separating Solids',
    'B08': 'Cleaning',
    'B09': 'Disposal of Solid Waste',
    'B21': 'Mechanical Metal-Working',
    'B22': 'Casting; Powder Metallurgy',
    'B23': 'Machine Tools',
    'B24': 'Grinding; Polishing',
    'B25': 'Hand Tools; Portable Power-Driven Tools',
    'B26': 'Hand Cutting Tools',
    'B27': 'Working or Preserving Wood',
    'B28': 'Working Cement, Clay, Stone',
    'B29': 'Working of Plastics',
    'B30': 'Presses',
    'B31': 'Making Paper Articles',
    'B32': 'Layered Products',
    'B33': 'Additive Manufacturing (3D Printing)',
    'B60': 'Vehicles in General',
    'B61': 'Railways',
    'B62': 'Land Vehicles for Travelling',
    'B63': 'Ships; Waterborne Vessels',
    'B64': 'Aircraft; Aviation; Cosmonautics',
    'B65': 'Conveying; Packing; Storing',
    'B66': 'Hoisting; Lifting; Hauling',
    'B67': 'Liquid Handling',
    'B68': 'Saddlery; Upholstery',
    'B81': 'Microstructural Technology',
    'B82': 'Nanotechnology',
    'C': 'Chemistry; Metallurgy',
    'C01': 'Inorganic Chemistry',
    'C02': 'Treatment of Water',
    'C03': 'Glass; Mineral or Slag Wool',
    'C04': 'Ite; Ceramics; Refractories',
    'C05': 'Fertilisers',
    'C06': 'Explosives; Matches',
    'C07': 'Organic Chemistry',
    'C08': 'Organic Macromolecular Compounds (Polymers)',
    'C09': 'Dyes; Paints; Adhesives',
    'C10': 'Petroleum, Gas or Coke Industries',
    'C11': 'Animal or Vegetable Oils, Fats',
    'C12': 'Biochemistry; Beer; Spirits; Microbiology',
    'C13': 'Sugar Industry',
    'C14': 'Skins; Hides; Pelts; Leather',
    'C21': 'Metallurgy of Iron',
    'C22': 'Metallurgy; Alloys',
    'C23': 'Coating Metallic Material',
    'C25': 'Electrolytic or Electrophoretic Processes',
    'C30': 'Crystal Growth',
    'C40': 'Combinatorial Technology',
    'D': 'Textiles; Paper',
    'D01': 'Natural or Man-Made Threads or Fibres',
    'D02': 'Yarns; Mechanical Finishing of Yarns',
    'D03': 'Weaving',
    'D04': 'Braiding; Lace-Making; Knitting',
    'D05': 'Sewing; Embroidering; Tufting',
    'D06': 'Treatment of Textiles',
    'D07': 'Ropes; Cables',
    'D21': 'Paper-Making',
    'E': 'Fixed Constructions',
    'E01': 'Construction of Roads, Railways',
    'E02': 'Hydraulic Engineering; Foundations',
    'E03': 'Water Supply; Sewerage',
    'E04': 'Building',
    'E05': 'Locks; Keys; Window or Door Fittings',
    'E06': 'Doors, Windows, Shutters',
    'E21': 'Earth or Rock Drilling; Mining',
    'F': 'Mechanical Engineering; Lighting; Heating; Weapons',
    'F01': 'Machines or Engines in General',
    'F02': 'Combustion Engines',
    'F03': 'Machines or Engines for Liquids',
    'F04': 'Positive-Displacement Machines for Liquids',
    'F05': 'Indexing Schemes for Engines',
    'F15': 'Fluid-Pressure Actuators',
    'F16': 'Engineering Elements or Units',
    'F17': 'Storing or Distributing Gases',
    'F21': 'Lighting',
    'F22': 'Steam Generation',
    'F23': 'Combustion Apparatus',
    'F24': 'Heating; Ranges; Ventilating',
    'F25': 'Refrigeration or Cooling',
    'F26': 'Drying',
    'F27': 'Furnaces; Kilns; Ovens',
    'F28': 'Heat Exchange in General',
    'F41': 'Weapons',
    'F42': 'Ammunition; Blasting',
    'G': 'Physics',
    'G01': 'Measuring; Testing',
    'G02': 'Optics',
    'G03': 'Photography; Cinematography',
    'G04': 'Horology (Timekeeping)',
    'G05': 'Controlling; Regulating',
    'G06': 'Computing; Calculating; Counting',
    'G07': 'Checking Devices',
    'G08': 'Signalling',
    'G09': 'Education; Cryptography; Display; Advertising',
    'G10': 'Musical Instruments; Acoustics',
    'G11': 'Information Storage',
    'G12': 'Instrument Details',
    'G16': 'Information and Communication Technology (ICT)',
    'G21': 'Nuclear Physics; Nuclear Engineering',
    'H': 'Electricity',
    'H01': 'Basic Electric Elements',
    'H02': 'Generation, Conversion, Distribution of Electric Power',
    'H03': 'Electronic Circuitry',
    'H04': 'Electric Communication Technique',
    'H05': 'Electric Techniques Not Otherwise Provided For',
    'H10': 'Semiconductor Devices; Electric Solid-State Devices',
    'Y': 'General Tagging of New Technologies',
    'Y02': 'Climate Change Mitigation Technologies',
    'Y04': 'Information or Communication Technologies',
    'Y10': 'Technical Subjects Covered by Former USPC',
  };

  // Function to get CPC description
  const getCpcDescription = (cpcClass) => {
    if (!cpcClass) return '';
    // Try exact match first (e.g., "H04")
    if (cpcDescriptions[cpcClass]) return cpcDescriptions[cpcClass];
    // Try first 3 characters (e.g., "H04" from "H04L")
    const threeChar = cpcClass.substring(0, 3);
    if (cpcDescriptions[threeChar]) return cpcDescriptions[threeChar];
    // Try first character (e.g., "H" from "H04")
    const oneChar = cpcClass.substring(0, 1);
    if (cpcDescriptions[oneChar]) return cpcDescriptions[oneChar];
    return 'Patent Classification Code';
  };

  // WIPO Sector Descriptions for tooltips
  const wipoDescriptions = {
    'Electrical engineering': 'Electrical machinery, apparatus, energy; Audio-visual technology; Telecommunications; Digital communication; Basic communication processes; Computer technology; IT methods for management; Semiconductors',
    'Instruments': 'Optics; Measurement; Analysis of biological materials; Control; Medical technology',
    'Chemistry': 'Organic fine chemistry; Biotechnology; Pharmaceuticals; Macromolecular chemistry; Food chemistry; Basic materials chemistry; Materials; Metallurgy; Surface technology; Micro-structure and nano-technology; Chemical engineering; Environmental technology',
    'Mechanical engineering': 'Handling; Machine tools; Engines; Pumps; Turbines; Textile and paper machines; Other special machines; Thermal processes; Mechanical elements; Transport',
    'Other fields': 'Furniture; Games; Other consumer goods; Civil engineering',
  };

  // Function to get WIPO description
  const getWipoDescription = (sector) => {
    if (!sector) return '';
    if (wipoDescriptions[sector]) return wipoDescriptions[sector];
    return 'WIPO Technology Sector';
  };



  const [companyLoading, setCompanyLoading] = useState(false);
  useEffect(() => {
  const token = localStorage.getItem('token');
  if (token) {
    loadUser(token);
    loadSavedPatents();
  }
}, []);

  useEffect(() => {
    if (menuOpen && user) {
      loadSavedSearches();
    }
  }, [menuOpen, user]);

  // Load saved prior art searches when viewing that page
  useEffect(() => {
    if (page === 'prior-art-searches' && user) {
      loadSavedPriorArtSearches();
    }
  }, [page, user]);

  const loadSavedPriorArtSearches = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(`${config.API_URL}/api/prior-art-searches?token=${token}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setSavedPriorArtSearches(data);
      } else {
        console.error('API did not return array:', data);
        setSavedPriorArtSearches([]);
      }
    } catch (error) {
      console.error('Error loading saved prior art searches:', error);
      setSavedPriorArtSearches([]);
    }
  };

  useEffect(() => {
  if (page === 'saved-patents' && user) {
    loadSavedPatents();
  }
  if (page === 'saved-comparisons' && user) {
    loadSavedComparisons();
  }
  if (page === 'saved-invention-assessments' && user) {
    loadSavedInventionAssessments();
  }
  if (page === 'saved-individual-reports' && user) {
    loadSavedIndividualReports();
  }
  if (page.startsWith('saved-reports-') && user) {
    loadSavedIndividualReports();
  }
}, [page]);

  const loadUser = async (token) => {
    try {
      const response = await fetch(`${config.API_URL}/api/me?token=${token}`);
      const data = await response.json();
      setUser(data);
      setUserUsage(data.usage);
      if (data.is_admin) {
        loadFreeMode(token);
        loadCacheStats(token);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      localStorage.removeItem('token');
    }
  };

  const loadFreeMode = async (token) => {
    try {
      const response = await fetch(`${config.API_URL}/api/admin/free-mode?token=${token}`);
      const data = await response.json();
      setFreeMode(data.free_mode);
    } catch (error) {
      console.error('Error loading free mode:', error);
    }
  };
const loadCacheStats = async (token) => {
  try {
    const response = await fetch(`${config.API_URL}/api/admin/cache-stats?token=${token}`);
    const data = await response.json();
    setCacheStats(data);
  } catch (error) {
    console.error('Error loading cache stats:', error);
  }
};
  const loadAllUsers = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/admin/users?token=${token}`);
      const data = await response.json();
      setAllUsers(Array.isArray(data) ? data : data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const toggleFreeMode = async () => {
  const token = localStorage.getItem('token');
  const newValue = !freeMode;
  
  try {
    const response = await fetch(`${config.API_URL}/api/admin/free-mode?token=${token}&enabled=${newValue}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    
    const data = await response.json();
    
    if (response.ok && data.success) {
      // Update state based on what backend actually saved
      setFreeMode(data.free_mode);
      alert(`Free mode ${data.free_mode ? 'enabled' : 'disabled'} successfully`);
    } else {
      console.error('Toggle failed:', data);
      alert('Failed to toggle free mode. Error: ' + JSON.stringify(data.detail || data));
    }
  } catch (error) {
    console.error('Error toggling free mode:', error);
    alert('Failed to toggle free mode: ' + error.message);
  }
};

  const handleLogin = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;

    try {
      const response = await fetch(`${config.API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('token', data.access_token);
        await loadUser(data.access_token);
        // Clear all search state on login (fresh start for new user)
        setQuery('');
        setResults([]);
        setHasSearched(false);
        setPriorArtResults(null);
        setSavedSearches([]);
        setSavedPriorArtSearches([]);
        setAppSearchQuery('');
        setAppSearchAssignee('');
        setAppSearchCpc('');
        setAppSearchResults(null);
        setCompanySearchName('');
        setPage('search');
      } else {
        alert(data.detail || 'Login failed');
      }
    } catch (error) {
      alert('Login failed: ' + error.message);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;
    const fullName = e.target.fullName.value;

    try {
      const response = await fetch(`${config.API_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, full_name: fullName })
      });
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('token', data.access_token);
        await loadUser(data.access_token);
        // Clear all search state on login (fresh start for new user)
        setQuery('');
        setResults([]);
        setPriorArtResults(null);
        setSavedSearches([]);
        setSavedPriorArtSearches([]);
        setAppSearchQuery('');
        setAppSearchAssignee('');
        setAppSearchCpc('');
        setAppSearchResults(null);
        setCompanySearchName('');
        setPage('search');
      } else {
        alert(data.detail || 'Registration failed');
      }
    } catch (error) {
      alert('Registration failed: ' + error.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    // Clear all search state on logout
    setQuery('');
    setResults([]);
    setHasSearched(false);
    setPriorArtResults(null);
    setSavedSearches([]);
    setSavedPriorArtSearches([]);
    setAppSearchQuery('');
    setAppSearchAssignee('');
    setAppSearchCpc('');
    setAppSearchResults(null);
    setCompanySearchName('');
    setPage('search');
  };

  const loadSavedSearches = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setSavedSearches([]);
        return;
      }
      const response = await fetch(`${config.API_URL}/api/saved-searches?token=${token}`);
      const data = await response.json();
      setSavedSearches(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading saved searches:', error);
      setSavedSearches([]);
    }
  };

  
  // Fetch citation counts when results change
  useEffect(() => {
    const fetchCitationCounts = async () => {
      if (!results || results.length === 0) {
        setCitationCounts({});
        return;
      }
      try {
        const patentNumbers = results.map(p => p.id).filter(id => id);
        if (patentNumbers.length === 0) return;
        
        const response = await fetch(`${config.API_URL}/api/citations/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ patent_numbers: patentNumbers })
        });
        if (response.ok) {
          const data = await response.json();
          setCitationCounts(data);
        }
      } catch (error) {
        console.error('Error fetching citation counts:', error);
      }
    };
    fetchCitationCounts();
  }, [results]);



  // Sort results based on selected option
  const getFilteredAndSortedResults = () => {
    if (!results || !results.length) return [];
    
    // First filter by status
    let filtered = results;
    if (statusFilter === 'granted') {
      filtered = results.filter(p => ['GRANTED', 'LIVE'].includes(p.status?.toUpperCase()));
    } else if (statusFilter === 'pending') {
      filtered = results.filter(p => p.status?.toUpperCase() === 'PENDING');
    }
    
    // Then sort
    if (sortBy === 'relevance') return filtered;
    
    const sorted = [...filtered];
    switch (sortBy) {
      case 'most_cited':
        sorted.sort((a, b) => {
          const aCount = citationCounts[a.id]?.forward_citations || 0;
          const bCount = citationCounts[b.id]?.forward_citations || 0;
          return bCount - aCount;
        });
        break;
      case 'newest':
        sorted.sort((a, b) => {
          const aDate = a.grant_date || '0000-00-00';
          const bDate = b.grant_date || '0000-00-00';
          return bDate.localeCompare(aDate);
        });
        break;
      case 'oldest':
        sorted.sort((a, b) => {
          const aDate = a.grant_date || '9999-99-99';
          const bDate = b.grant_date || '9999-99-99';
          return aDate.localeCompare(bDate);
        });
        break;
      default:
        break;
    }
    return sorted;
  };

  // Fetch citation details when expanding
  const handleExpandCitations = async (patentId) => {
    if (expandedCitations === patentId) {
      setExpandedCitations(null);
      return;
    }
    setExpandedCitations(patentId);
    setCitationDetailsLoading(true);
    setCitationDetails({ forward: [], backward: [] });
    
    try {
      const [forwardRes, backwardRes] = await Promise.all([
        fetch(`${config.API_URL}/api/citations/${patentId}/forward?limit=20`),
        fetch(`${config.API_URL}/api/citations/${patentId}/backward?limit=20`)
      ]);
      
      const forwardData = await forwardRes.json();
      const backwardData = await backwardRes.json();
      
      setCitationDetails({
        forward: forwardData.citations || [],
        backward: backwardData.citations || []
      });
    } catch (error) {
      console.error('Error fetching citation details:', error);
    }
    setCitationDetailsLoading(false);
  };

  const handleExpandFamily = async (patentId) => {
    if (expandedFamily === patentId) {
      setExpandedFamily(null);
      return;
    }
    setExpandedFamily(patentId);
    setFamilyLoading(true);
    setFamilyData({ foreign_priorities: [], family_members: [] });

    try {
      const response = await fetch(`${config.API_URL}/api/patent-family/${patentId}`);
      const data = await response.json();
      setFamilyData({
        foreign_priorities: data.foreign_priorities || [],
        family_members: data.family_members || []
      });
    } catch (error) {
      console.error('Error fetching patent family:', error);
    }
    setFamilyLoading(false);
  };

const handleCompanySelection = async (selectedCompany) => {
    setShowCompanyDisambiguation(false);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const url = token
        ? `${config.API_URL}/api/search-db?query=${encodeURIComponent(selectedCompany)}&status=${statusFilter}&assignee_only=true&token=${token}`
        : `${config.API_URL}/api/search-db?query=${encodeURIComponent(selectedCompany)}&status=${statusFilter}&assignee_only=true`;
      
      const response = await fetch(url);
      const data = await response.json();
      setResults(data.results);
      setHasSearched(true);
      setSortBy('relevance');
      setTotalCount(data.total_count || data.count || 0);
      setGrantedCount(data.granted_count || 0);
setPendingCount(data.pending_count || 0);
      setTransparency(data.transparency || null);
      setTopAssignees(data.top_assignees || []);
      
      // Fetch landscape data
      setLandscapeLoading(true);
      try {
        const landscapeUrl = token
          ? `${config.API_URL}/api/landscape-summary?query=${encodeURIComponent(selectedCompany)}&search_type=assignee&token=${token}`
          : `${config.API_URL}/api/landscape-summary?query=${encodeURIComponent(selectedCompany)}&search_type=assignee`;
        const landscapeResponse = await fetch(landscapeUrl);
        const landscapeResult = await landscapeResponse.json();
        setLandscapeData(landscapeResult);
      } catch (landscapeError) {
        console.error('Landscape fetch error:', landscapeError);
        setLandscapeData(null);
      }
      setLandscapeLoading(false);
      
      setQuery(selectedCompany);
    } catch (error) {
      console.error('Search error:', error);
      alert('Search failed: ' + error.message);
    }
    setLoading(false);
  };

  const handleSearch = async (e, savedQuery = null, savedSource = null) => {
    if (e) e.preventDefault();
    // Check if AI search requires login
    if (searchType === 'ai' && !localStorage.getItem('token')) {
      setShowLoginPrompt(true);
      setLoginPromptMessage('Register free to use AI-powered search. AI Search understands natural language and finds patents you might miss with keyword search.');
      return;
    }

    const searchQuery = savedQuery || query;
    const searchSource = savedSource || source;

    setLoading(true);
    setMenuOpen(false);
    setPage('search');
    setPatentSummaries({});
    setTransparency(null);
    setResults([]);
    setShowTransparency(true);

    try {
      const token = localStorage.getItem('token');
      let url;
      
      if (searchType === 'patent') {
        url = token
          ? `${config.API_URL}/api/search-db?query=${encodeURIComponent(searchQuery)}&status=${statusFilter}&patent_only=true&token=${token}`
          : `${config.API_URL}/api/search-db?query=${encodeURIComponent(searchQuery)}&status=${statusFilter}&patent_only=true`;
      } else if (searchType === 'assignee') {
        // First check if we need disambiguation
        const disambigResponse = await fetch(`${config.API_URL}/api/search/disambiguate-assignee?query=${encodeURIComponent(searchQuery)}`);
        const disambigData = await disambigResponse.json();
        
        if (disambigData.needs_disambiguation && disambigData.matches.length > 1) {
          // Multiple companies found - show selection modal
          setCompanyMatches(disambigData.matches);
          setPendingCompanySearch(searchQuery);
          setShowCompanyDisambiguation(true);
          setLoading(false);
          return; // Stop here, let user pick
        }
        
        // Single match or no disambiguation needed - proceed with search
        const exactCompany = disambigData.matches.length === 1 ? disambigData.matches[0].assignee : searchQuery;
        url = token
          ? `${config.API_URL}/api/search-db?query=${encodeURIComponent(exactCompany)}&status=${statusFilter}&assignee_only=true&token=${token}`
          : `${config.API_URL}/api/search-db?query=${encodeURIComponent(exactCompany)}&status=${statusFilter}&assignee_only=true`;
      } else if (searchType === 'inventor') {
        url = token
          ? `${config.API_URL}/api/search-db?query=${encodeURIComponent(searchQuery)}&status=${statusFilter}&inventor_only=true&token=${token}`
          : `${config.API_URL}/api/search-db?query=${encodeURIComponent(searchQuery)}&status=${statusFilter}&inventor_only=true`;
      } else {
        url = token
          ? `${config.API_URL}/api/search-db?query=${encodeURIComponent(searchQuery)}&status=${statusFilter}&token=${token}`
          : `${config.API_URL}/api/search-db?query=${encodeURIComponent(searchQuery)}&status=${statusFilter}`;
      }

      const response = await fetch(url);
      
      // Check for limit exceeded error (429)
      if (!response.ok) {
        const errorData = await response.json();
        
        if (response.status === 429) {
          // User hit their daily limit - show upgrade prompt
          setShowLoginPrompt(true);
          setLoginPromptMessage(errorData.detail || "You've reached your daily limit of AI searches. Upgrade for unlimited searches!");
          setLoading(false);
          return;
        }
        
        // Other errors - throw to be caught below
        throw new Error(errorData.detail || 'Search failed');
      }
      
      const data = await response.json();
      setResults(data.results);
      setHasSearched(true);
      setSortBy('relevance');
      setTotalCount(data.total_count || data.count || 0);
setGrantedCount(data.granted_count || 0);
setPendingCount(data.pending_count || 0);
      setTransparency(data.transparency || null);
      setTopAssignees(data.top_assignees || []);
// Update AI search usage counter if returned
      if (data.remaining_searches !== undefined && userUsage) {
        setUserUsage(prev => ({
          ...prev,
          ai_searches: {
            ...prev.ai_searches,
            used: prev.ai_searches.limit - data.remaining_searches,
            remaining: data.remaining_searches
          }
        }));
      }
      // Fetch landscape data for the search results
      if (searchType === 'ai' || searchType === 'assignee' || searchType === 'inventor') {
        setLandscapeLoading(true);
        try {
          const landscapeUrl = token
            ? `${config.API_URL}/api/landscape-summary?query=${encodeURIComponent(searchQuery)}&search_type=${searchType}&token=${token}`
            : `${config.API_URL}/api/landscape-summary?query=${encodeURIComponent(searchQuery)}&search_type=${searchType}`;
          const landscapeResponse = await fetch(landscapeUrl);
          const landscapeResult = await landscapeResponse.json();
          setLandscapeData(landscapeResult);
        } catch (landscapeError) {
          console.error('Landscape fetch error:', landscapeError);
          setLandscapeData(null);
        }
        setLandscapeLoading(false);
      } else {
        setLandscapeData(null);
      }
      setQuery(searchQuery);
      setSource(searchSource);
    } catch (error) {
      console.error('Search error:', error);
      alert('Search failed: ' + error.message);
    }
    setLoading(false);
  };

  const handlePriorArtSearch = async (e) => {
    e.preventDefault();
    
    // Validate that at least one key feature is provided
    const validFeatures = priorArtForm.key_features.filter(f => f.trim() !== '');
    if (validFeatures.length === 0) {
      alert('Please enter at least one Key Feature.\n\nKey features are essential for generating a proper Claim Chart - a feature-by-feature comparison that patent attorneys use to assess novelty.');
      return;
    }
    
    setPriorArtLoading(true);
    setPriorArtResults(null);
    setPriorArtProgress({ progress: 0, stage: 'Starting analysis...' });

    try {
      const token = localStorage.getItem('token');
      
      // Use streaming endpoint for real-time progress
      const response = await fetch(`${config.API_URL}/api/prior-art-search-stream?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invention_title: priorArtForm.invention_title,
          invention_description: priorArtForm.invention_description,
          distilled_claim: priorArtForm.distilled_claim,
          priority_date: priorArtForm.priority_date,
          jurisdiction: priorArtForm.jurisdiction,
          report_type: priorArtForm.report_type,
          key_features: priorArtForm.key_features.filter(f => f.trim() !== ''),
          known_competitors: priorArtForm.known_competitors ? [priorArtForm.known_competitors] : []
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || '';

        for (const message of messages) {
          if (message.startsWith('data: ')) {
            try {
              const data = JSON.parse(message.slice(6));
              
              if (data.error) {
                alert('Prior art search failed: ' + data.error);
                setPriorArtLoading(false);
                setPriorArtProgress({ progress: 0, stage: '' });
                return;
              }
              
              setPriorArtProgress({ progress: data.progress, stage: data.stage });
              
              if (data.result) {
                setPriorArtResults(data.result);
              }
            } catch (e) {
              console.error('SSE parse error:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Prior art search error:', error);
      alert('Prior art search failed: ' + error.message);
    }
    setPriorArtLoading(false);
    setPriorArtProgress({ progress: 0, stage: '' });
  };

const saveSearch = async () => {
    if (!query || results.length === 0) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to save searches');
      return;
    }
    
    try {
      const response = await fetch(`${config.API_URL}/api/save-search?query=${encodeURIComponent(query)}&source=${source}&result_count=${results.length}&token=${token}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (response.ok) {
        alert('Search saved!');
      } else if (response.status === 403) {
        // Limit reached - show upgrade modal
        setLoginPromptMessage(data.detail || "You've reached your saved searches limit. Upgrade for more.");
        setShowLoginPrompt(true);
      } else {
        alert('Failed to save search: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving search:', error);
      alert('Error saving search: ' + error.message);
    }
  };

  // NEW: Tiered Analysis Function (Phase 4)
  const handleTieredAnalysis = async (patentId, tier, title, abstract, status) => {
    const token = localStorage.getItem('token');
    
    // Check if user is logged in
    if (!token) {
      setLoginPromptMessage('Please log in to use patent analysis features.');
      setShowLoginPrompt(true);
      return;
    }
    // Set loading state for this specific patent + tier
    const loadingKey = `${patentId}-${tier}`;
    setAnalysisLoading(prev => ({ ...prev, [loadingKey]: true }));
    
    try {
      const response = await fetch(`${config.API_URL}/api/analyze-tiered?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patent_number: patentId,
          tier: tier,
          title: title,
          abstract: abstract,
          status: status
        })
      });
      
      const data = await response.json();
      
      // Handle tier-locked or limit reached
      if (response.status === 403 || response.status === 429) {
        const tierNames = { quick: 'Quick', standard: 'Standard', deep: 'Deep' };
        const upgradeTier = tier === 'deep' ? 'Pro' : 'Premium';
        
        if (response.status === 403) {
          // Feature locked
          setLoginPromptMessage(`${tierNames[tier]} Analysis requires ${upgradeTier}. Upgrade to unlock ${tier === 'deep' ? 'comprehensive technical, commercial and legal insights' : 'deeper patent analysis with claim mapping'}.`);
        } else {
          // Limit reached
          setLoginPromptMessage(data.detail || `You've reached your daily limit for ${tierNames[tier]} Analysis. Upgrade to ${upgradeTier} for more analyses.`);
        }
        setShowLoginPrompt(true);
        return;
      }
      
      if (data.success) {
        setPatentSummaries(prev => ({
          ...prev,
          [patentId]: {
            text: data.analysis,
            visible: true,
            loading: false,
            tier: tier,
            tierName: data.tier_name,
            fromCache: data.from_cache,
            tokensUsed: data.tokens_used,
            cost: data.cost
          }
        }));
        
        // Update usage if returned
        if (data.remaining_today !== undefined && userUsage) {
          setUserUsage(prev => ({
            ...prev,
            [`${tier}_analyses`]: {
              ...prev[`${tier}_analyses`],
              used: prev[`${tier}_analyses`].limit - data.remaining_today,
              remaining: data.remaining_today
            }
          }));
        }
      } else {
        alert('Analysis failed. Please try again.');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      alert('Failed to generate analysis. Please check your connection and try again.');
    } finally {
      setAnalysisLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };
  
  // NEW: Function to handle patent explanation toggle
  const handleExplainPatent = async (patentId, title, abstract, status) => {
    // If summary already exists, toggle visibility
    if (patentSummaries[patentId]) {
      setPatentSummaries(prev => ({
        ...prev,
        [patentId]: {
          ...prev[patentId],
          visible: !prev[patentId].visible
        }
      }));
    } else {
      // Set loading state
      setPatentSummaries(prev => ({
        ...prev,
        [patentId]: {
          loading: true,
          visible: false
        }
      }));
      
      // Fetch new summary
      try {
        const response = await fetch(`${config.API_URL}/api/summarize-patent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: title, abstract: abstract, status: status, patent_number: patentId })
        });
        const data = await response.json();
        
        setPatentSummaries(prev => ({
          ...prev,
          [patentId]: {
            text: data.summary,
            visible: true,
            loading: false
          }
        }));
      } catch (error) {
        alert('Failed to generate summary');
        // Clear loading state on error
        setPatentSummaries(prev => ({
          ...prev,
          [patentId]: {
            loading: false,
            visible: false
          }
        }));
      }
    }
  };
  
// Compare functionality
  const addToCompare = (patent) => {
    // Tier limits: anonymous=must login, freemium=2, premium/pro=3
    if (!user) {
      setLoginPromptMessage('Please log in to compare patents.');
      setShowLoginPrompt(true);
      return;
    }
    const maxCompare = user.tier === 'free' ? 2 : 3;
    if (compareList.find(p => p.id === patent.id)) {
      alert('This patent is already in your comparison list');
      return;
    }
    if (compareList.length >= maxCompare) {
      if (user.tier === 'free') {
        setLoginPromptMessage('Free accounts can compare up to 2 patents. Upgrade to Premium to compare 3 patents.');
        setShowLoginPrompt(true);
      } else {
        alert('Maximum 3 patents can be compared at once');
      }
      return;
    }
    // Ensure we capture all fields including cpc_codes
    const patentData = {
      id: patent.id,
      title: patent.title,
      summary: patent.summary,
      abstract: patent.abstract || patent.summary,
      assignee: patent.assignee,
      filing_date: patent.filing_date,
      grant_date: patent.grant_date,
      status: patent.status,
      ipc_codes: patent.ipc_codes || [],
      cpc_codes: patent.cpc_codes || []
    };
    console.log('Adding to compare:', patentData);
    setCompareList(prev => [...prev, patentData]);
  };

  const removeFromCompare = (patentId) => {
    setCompareList(prev => prev.filter(p => p.id !== patentId));
  };

  const clearCompare = () => {
    setCompareList([]);
    setShowComparePanel(false);
  };
const openShareModal = async (patent) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to share patents');
      return;
    }
    if (user?.tier === 'free') {
      alert('Workspaces require a Premium or Pro subscription');
      return;
    }
    setPatentToShare(patent);
    setShareNote('');
    // Load user's workspaces
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces?token=${token}`);
      const data = await response.json();
      if (response.ok) {
        setUserWorkspaces(data);
        setShowShareModal(true);
      }
    } catch (error) {
      console.error('Error loading workspaces:', error);
    }
  };

  const shareToWorkspace = async (workspaceId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${config.API_URL}/api/workspaces/${workspaceId}/patents?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patent_number: patentToShare.id, note: shareNote })
      });
      const data = await response.json();
      if (response.ok) {
        alert(data.message);
        setShowShareModal(false);
        setPatentToShare(null);
        setShareNote('');
        setSelectedWorkspaceId(null);
      } else {
        alert(data.detail || 'Failed to share patent');
      }
    } catch (error) {
      alert('Error sharing patent');
    }
  };

const handleSavePatent = async (patent) => {
  const token = localStorage.getItem('token');
  if (!token) {
    alert('Please log in to save patents');
    return;
  }
  setSavingPatent(patent.id);
  try {
    // Send as query parameters, not JSON body
    const params = new URLSearchParams({
      patent_number: patent.id,
      title: patent.title,
      abstract: patent.summary,
      assignee: patent.assignee || 'Unknown',
      token: token
    });
    const response = await fetch(`${config.API_URL}/api/save-patent?${params}`, {
      method: 'POST',
    });
    const data = await response.json();
    
    if (response.ok) {
      setSavedPatents(prev => new Set([...prev, patent.id]));
      if (data.already_saved) {
        alert('This patent is already in your saved collection');
      }
    } else if (response.status === 403) {
      // Limit reached - show upgrade modal
      setLoginPromptMessage(data.detail || "You've reached your saved patents limit. Upgrade for more.");
      setShowLoginPrompt(true);
    } else {
      console.error('Save failed:', data);
      alert('Failed to save patent: ' + (data.detail || 'Unknown error'));
    }
  } catch (error) {
    console.error('Error saving patent:', error);
    alert('Error saving patent: ' + error.message);
  } finally {
    setSavingPatent(null);
  }
};

// Enhanced button handlers for Company Search
const handleViewClaims = async (patentId, status) => {
  if (!user || user.tier !== 'pro') {
    setLoginPromptMessage(user 
      ? 'View Claims requires Pro subscription. Upgrade to access detailed patent claims.'
      : 'Please log in to access View Claims.');
    setShowLoginPrompt(true);
    return null;
  }
  try {
    const token = localStorage.getItem('token');
    const isPending = status?.toUpperCase() === 'PENDING';
    const endpoint = isPending ? `/api/application-claims/${patentId}` : `/api/patent-claims/${patentId}`;
    const response = await fetch(`${config.API_URL}${endpoint}?token=${token}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching claims:', error);
    alert('Failed to fetch claims. Please try again.');
    return null;
  }
};

const handleViewSummary = async (patentId, status) => {
  if (!user || user.tier !== 'pro') {
    setLoginPromptMessage(user 
      ? 'View Summary requires Pro subscription. Upgrade to access AI-generated summaries.'
      : 'Please log in to access View Summary.');
    setShowLoginPrompt(true);
    return null;
  }
  try {
    const token = localStorage.getItem('token');
    const isPending = status?.toUpperCase() === 'PENDING';
    const endpoint = isPending ? `/api/application-summary/${patentId}` : `/api/patent-summary/${patentId}`;
    const response = await fetch(`${config.API_URL}${endpoint}?token=${token}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching summary:', error);
    alert('Failed to fetch summary. Please try again.');
    return null;
  }
};

const handleViewPCT = async (patentId, status) => {
  if (!user || user.tier !== 'pro') {
    setLoginPromptMessage(user 
      ? 'View PCT requires Pro subscription. Upgrade to access international patent data.'
      : 'Please log in to access View PCT.');
    setShowLoginPrompt(true);
    return null;
  }
  try {
    const token = localStorage.getItem('token');
    const isPending = status?.toUpperCase() === 'PENDING';
    const endpoint = isPending ? `/api/application-pct/${patentId}` : `/api/patent-pct/${patentId}`;
    const response = await fetch(`${config.API_URL}${endpoint}?token=${token}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching PCT:', error);
    alert('Failed to fetch PCT data. Please try again.');
    return null;
  }
};

const handleViewDescription = async (patentId, status) => {
  if (!user || user.tier !== 'pro') {
    setLoginPromptMessage(user 
      ? 'View Description requires Pro subscription. Upgrade to access full patent descriptions.'
      : 'Please log in to access View Description.');
    setShowLoginPrompt(true);
    return null;
  }
  try {
    const token = localStorage.getItem('token');
    const isPending = status?.toUpperCase() === 'PENDING';
    const endpoint = isPending ? `/api/application-description/${patentId}` : `/api/patent-description/${patentId}`;
    const response = await fetch(`${config.API_URL}${endpoint}?token=${token}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching description:', error);
    alert('Failed to fetch description. Please try again.');
    return null;
  }
};
const loadSavedPatents = async () => {
  const token = localStorage.getItem('token');
  if (!token) return;

  try {
    const response = await fetch(`${config.API_URL}/api/saved-patents?token=${token}`);
    const data = await response.json();
    
    if (response.ok) {
      setSavedPatents(new Set(data.map(p => p.patent_number)));
      setViewSavedPatents(data);
    }
  } catch (error) {
    console.error('Error loading saved patents:', error);
  }
};
const loadSavedComparisons = async () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const response = await fetch(`${config.API_URL}/api/saved-comparisons?token=${token}`);
    const data = await response.json();
    if (response.ok) {
      setSavedComparisons(data);
    }
  } catch (error) {
    console.error('Error loading saved comparisons:', error);
  }
};

const loadSavedInventionAssessments = async () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const response = await fetch(`${config.API_URL}/api/saved-invention-assessments?token=${token}`);
    const data = await response.json();
    if (response.ok && Array.isArray(data)) {
      setSavedInventionAssessments(data);
    }
  } catch (error) {
    console.error('Error loading saved invention assessments:', error);
  }
};

const loadSavedIndividualReports = async () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const response = await fetch(`${config.API_URL}/api/saved-individual-reports?token=${token}`);
    const data = await response.json();
    if (response.ok && Array.isArray(data)) {
      setSavedIndividualReports(data);
    }
  } catch (error) {
    console.error('Error loading saved individual reports:', error);
  }
};

const deleteSavedInventionAssessment = async (id) => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const response = await fetch(`${config.API_URL}/api/saved-invention-assessments/${id}?token=${token}`, {
      method: 'DELETE'
    });
    if (response.ok) {
      setSavedInventionAssessments(prev => prev.filter(a => a.id !== id));
    }
  } catch (error) {
    console.error('Error deleting assessment:', error);
  }
};

const deleteSavedIndividualReport = async (id) => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const response = await fetch(`${config.API_URL}/api/saved-individual-reports/${id}?token=${token}`, {
      method: 'DELETE'
    });
    if (response.ok) {
      setSavedIndividualReports(prev => prev.filter(r => r.id !== id));
    }
  } catch (error) {
    console.error('Error deleting report:', error);
  }
};

const deleteSavedSearch = async (id) => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const response = await fetch(`${config.API_URL}/api/saved-searches/${id}?token=${token}`, {
      method: 'DELETE'
    });
    if (response.ok) {
      setSavedSearches(prev => prev.filter(s => s.id !== id));
    }
  } catch (error) {
    console.error('Error deleting saved search:', error);
  }
};

const handleUnsavePatent = async (patentNumber) => {
  const token = localStorage.getItem('token');
  if (!token) return;

  try {
    const response = await fetch(`${config.API_URL}/api/unsave-patent?patent_number=${patentNumber}&token=${token}`, {
      method: 'DELETE',
    });

    if (response.ok) {
      setSavedPatents(prev => {
        const newSet = new Set(prev);
        newSet.delete(patentNumber);
        return newSet;
      });
      setViewSavedPatents(prev => prev.filter(p => p.patent_number !== patentNumber));
    } else {
      alert('Failed to remove patent');
    }
  } catch (error) {
    console.error('Error removing patent:', error);
    alert('Error removing patent');
  }
};

  // LOGIN PAGE
  if (page === 'login') {
    return <LoginPage setPage={setPage} handleLogin={handleLogin} />;
  }

  // REGISTER PAGE
  if (page === 'register') {
    return <RegisterPage setPage={setPage} handleRegister={handleRegister} />;
  }

  // ADMIN PAGE
  if (page === 'admin' && user?.is_admin) {
    return <AdminPage setPage={setPage} user={user} freeMode={freeMode} toggleFreeMode={toggleFreeMode} cacheStats={cacheStats} allUsers={allUsers} loadAllUsers={loadAllUsers} />;
  }

  // PRICING PAGE
  // Workspaces page - collaborative workspaces
  if (page === 'workspaces' && user) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
        <header style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={() => setPage('search')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d2934', fontSize: '16px' }}>
              ? Back to Search
            </button>
            <img src="/logo.svg" alt="PatTrak Logo" style={{ height: '32px', cursor: 'pointer' }} onClick={() => setPage('search')} />
          </div>
          <button onClick={handleLogout} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d2934' }}>
            Logout
          </button>
        </header>

        <WorkspacesContent user={user} />
      </div>
    );
  }

    // Dashboard page - user account overview
  if (page === 'dashboard' && user) {
    return <DashboardPage setPage={setPage} handleLogout={handleLogout} user={user} savedSearches={savedSearches} />;
  }

    if (page === 'pricing') {
    return <PricingPage setPage={setPage} user={user} handleLogout={handleLogout} />;
  }

  if (page === 'saved-workspace-comparisons') {
  return <SavedWorkspaceComparisons setPage={setPage} />;
  }

    // FAQ PAGE

  if (page === 'faq') {
    return <FAQPage setPage={setPage} user={user} handleLogout={handleLogout} />;
  }

  // PRIOR ART SEARCH PAGE
  if (page === 'prior-art') {
    // Check for prefill data from Invention Assessment
    const prefillData = localStorage.getItem('prior_art_prefill');
    if (prefillData) {
      try {
        const data = JSON.parse(prefillData);
        if (data.title && priorArtForm.invention_title === '') {
          // Map Invention Assessment fields to Prior Art fields:
          // title ? invention_title
          // description ? invention_description
          // novelty (what's new) ? distilled_claim
          // features ? key_features array
          // field ? added to first key feature if no features provided
          
          let keyFeatures = ['', '', ''];
          if (data.features) {
            // Split features by comma, newline, or semicolon
            const featureList = data.features.split(/[,;\n]/).map(f => f.trim()).filter(f => f);
            keyFeatures = [...featureList, '', '', ''].slice(0, 3);
          } else if (data.field) {
            keyFeatures = [data.field, '', ''];
          }
          
          setPriorArtForm(prev => ({
            ...prev,
            invention_title: data.title || '',
            invention_description: data.description || '',
            distilled_claim: data.novelty || '',
            key_features: keyFeatures
          }));
        }
        localStorage.removeItem('prior_art_prefill');
      } catch (e) {
        console.error('Error parsing prefill data:', e);
      }
    }
    return (
      <PriorArtPage
        priorArtForm={priorArtForm}
        setPriorArtForm={setPriorArtForm}
        handlePriorArtSearch={handlePriorArtSearch}
        priorArtLoading={priorArtLoading}
        priorArtProgress={priorArtProgress}
        priorArtResults={priorArtResults}
        setPriorArtResults={setPriorArtResults}
        setPage={setPage}
        user={user}
        handleLogout={handleLogout}
      />
    );
  }

    // SAVED PATENTS PAGE
  if (page === 'saved-patents') {
    return <SavedPatentsPage setPage={setPage} viewSavedPatents={viewSavedPatents} handleUnsavePatent={handleUnsavePatent} />;
  }

  if (page === 'saved-searches') {
    return <SavedSearchesPage setPage={setPage} savedSearches={savedSearches} handleSearch={handleSearch} deleteSavedSearch={deleteSavedSearch} />;
  }

// SAVED COMPARISONS PAGE
  if (page === 'saved-comparisons') {
    return <SavedComparisonsPage setPage={setPage} savedComparisons={savedComparisons} setCompareList={setCompareList} setShowComparePanel={setShowComparePanel} setSavedAiAnalysis={setSavedAiAnalysis} loadSavedComparisons={loadSavedComparisons} />;
  }
// COMPARISON WORKSPACE PAGE
if (page === 'comparison-workspace') {
  return <ComparisonWorkspace user={user} setPage={setPage} />;
}
  // COMPANY INTELLIGENCE PAGE
if (page === 'citations') {
    return <CitationAnalysisPage userUsage={userUsage} />;
  }
  if (page === 'network') {
    return <CitationNetworkPage userUsage={userUsage} />;
  }
  
  if (page === 'patent-decoder') {
    return <PatentDecoderPage user={user} onBack={() => setPage('search')} setPage={setPage} onUsageUpdate={() => loadUser(localStorage.getItem('token'))} />;
  }
  
  if (page === 'patentability-check') {
    return <PatentabilityCheckPage user={user} onBack={() => setPage('search')} setPage={setPage} onUsageUpdate={() => loadUser(localStorage.getItem('token'))} />;
  }
  
  if (page === 'fto-assessment') {
    return <FTOAssessmentPage user={user} onBack={() => setPage('search')} setPage={setPage} onUsageUpdate={() => loadUser(localStorage.getItem('token'))} />;
  }
  
  if (page === 'tech-landscape') {
    return <TechLandscapePage user={user} onBack={() => setPage('search')} setPage={setPage} onUsageUpdate={() => loadUser(localStorage.getItem('token'))} />;
  }
  
  if (page === 'invention-assessment') {
    return <InventionAssessmentPage user={user} onBack={() => setPage('search')} setPage={setPage} setShowLoginPrompt={setShowLoginPrompt} setLoginPromptMessage={setLoginPromptMessage} onUsageUpdate={() => loadUser(localStorage.getItem('token'))} />;
  }

  if (page === 'prior-art-deep-dive') {
    return <PriorArtDeepDivePage user={user} onBack={() => setPage('search')} setPage={setPage} onUsageUpdate={() => loadUser(localStorage.getItem('token'))} />;
  }

  if (page === 'fto-pro') {
    return <FTOProPage user={user} onBack={() => setPage('search')} setPage={setPage} onUsageUpdate={() => loadUser(localStorage.getItem('token'))} />;
  }

  if (page === 'competitive-intel') {
    return <CompetitiveIntelligencePage user={user} onBack={() => setPage('search')} setPage={setPage} onUsageUpdate={() => loadUser(localStorage.getItem('token'))} />;
  }

  if (page === 'saved-invention-assessments') {
    return <SavedInventionAssessmentsPage setPage={setPage} savedInventionAssessments={savedInventionAssessments} deleteSavedInventionAssessment={deleteSavedInventionAssessment} />;
  }

  if (page === 'saved-individual-reports') {
    return <SavedIndividualReportsPage setPage={setPage} savedIndividualReports={savedIndividualReports} deleteSavedIndividualReport={deleteSavedIndividualReport} reportTypeFilter="all" />;
  }

  if (page === 'saved-reports-patent-decoder') {
    return <SavedIndividualReportsPage setPage={setPage} savedIndividualReports={savedIndividualReports} deleteSavedIndividualReport={deleteSavedIndividualReport} reportTypeFilter="patent-decoder" />;
  }

  if (page === 'saved-reports-patentability-check') {
    return <SavedIndividualReportsPage setPage={setPage} savedIndividualReports={savedIndividualReports} deleteSavedIndividualReport={deleteSavedIndividualReport} reportTypeFilter="patentability-check" />;
  }

  if (page === 'saved-reports-fto-assessment') {
    return <SavedIndividualReportsPage setPage={setPage} savedIndividualReports={savedIndividualReports} deleteSavedIndividualReport={deleteSavedIndividualReport} reportTypeFilter="fto-assessment" />;
  }

  if (page === 'saved-reports-tech-landscape') {
    return <SavedIndividualReportsPage setPage={setPage} savedIndividualReports={savedIndividualReports} deleteSavedIndividualReport={deleteSavedIndividualReport} reportTypeFilter="tech-landscape" />;
  }

  if (page === 'saved-reports-prior-art-deep-dive') {
    return <SavedIndividualReportsPage setPage={setPage} savedIndividualReports={savedIndividualReports} deleteSavedIndividualReport={deleteSavedIndividualReport} reportTypeFilter="prior-art-deep-dive" />;
  }

  if (page === 'saved-reports-competitive-intelligence') {
    return <SavedIndividualReportsPage setPage={setPage} savedIndividualReports={savedIndividualReports} deleteSavedIndividualReport={deleteSavedIndividualReport} reportTypeFilter="competitive-intelligence" />;
  }

  if (page === 'saved-reports-fto-pro') {
    return <SavedIndividualReportsPage setPage={setPage} savedIndividualReports={savedIndividualReports} deleteSavedIndividualReport={deleteSavedIndividualReport} reportTypeFilter="fto-pro" />;
  }
  if (page === 'saved-reports-prior-art-pro') {
    return <SavedIndividualReportsPage setPage={setPage} savedIndividualReports={savedIndividualReports} deleteSavedIndividualReport={deleteSavedIndividualReport} reportTypeFilter="prior-art-pro" />;
  }

  if (page === 'company-intel') {
    return <CompanyIntelPage setPage={setPage} companySearchName={companySearchName} setCompanySearchName={setCompanySearchName} companyLoading={companyLoading} setCompanyLoading={setCompanyLoading} companyIntel={companyIntel} setCompanyIntel={setCompanyIntel} userUsage={userUsage} getCpcDescription={getCpcDescription} getWipoDescription={getWipoDescription} />;
  }

  if (page === 'prior-art-searches') {
    return <SavedPriorArtSearchesPage setPage={setPage} savedPriorArtSearches={savedPriorArtSearches} setSavedPriorArtSearches={setSavedPriorArtSearches} setPriorArtForm={setPriorArtForm} setPriorArtResults={setPriorArtResults} />;
  }

  // MAIN SEARCH PAGE
  return (
    <div style={{ minHeight: '100vh', background: 'white' }}>
      <Header
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        user={user}
        setPage={setPage}
        handleLogout={handleLogout}
        setShowTour={setShowTour}
      />

      <main style={{ maxWidth: '800px', margin: '80px auto', padding: '0 16px' }}>
        <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#1d2934', textAlign: 'center', marginBottom: '8px' }}>
          PatTrak
        </h1>
        <p style={{ fontSize: '18px', color: '#5e778a', textAlign: 'center', marginBottom: '32px', fontStyle: 'italic' }}>
          Decoding inventions in patents
        </p>

        <div style={{ marginBottom: '16px', textAlign: 'center' }} data-tour="database-selector">
          <label style={{ marginRight: '16px', color: '#1d2934' }}>
            <input 
              type="radio" 
              value="local" 
              checked={source === 'local'} 
              onChange={(e) => setSource(e.target.value)}
              style={{ marginRight: '4px' }}
            />
            Local Database
          </label>
          <label style={{ color: '#1d2934' }}>
            <input 
              type="radio" 
              value="uspto" 
              checked={source === 'uspto'} 
              onChange={(e) => setSource(e.target.value)}
              style={{ marginRight: '4px' }}
            />
            Espacenet (Real Patents)
          </label>
        </div>
        <div style={{ marginBottom: '16px', textAlign: 'center' }} data-tour="patent-status">
          <label style={{ color: '#1d2934', fontSize: '16px', marginRight: '8px' }}>Patent Status:</label>
          <select 
            value={patentStatus} 
            onChange={(e) => setPatentStatus(e.target.value)}
            style={{ 
              padding: '8px 16px', 
              border: '2px solid #5e778a', 
              borderRadius: '4px',
              fontSize: '16px',
              cursor: 'pointer',
              background: 'white'
            }}
          >
            <option value="both">Both</option>
            <option value="live">Live Only</option>
            <option value="lapsed">Lapsed Only</option>
          </select>
        </div>

        {/* Search Type Selector - Responsive */}
        <div style={{ marginBottom: '20px' }}>
          {/* Primary Search Types */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: '8px', 
            justifyContent: 'center',
            marginBottom: '12px'
          }}
          data-tour="search-types"
          >
            {[
              { value: 'ai', label: 'AI Search', icon: '?', unlimited: false },
              { value: 'patent', label: 'Patent #', icon: null, unlimited: true },
              { value: 'assignee', label: 'Company', icon: null, unlimited: true },
              { value: 'inventor', label: 'Inventor', icon: null, unlimited: true }
            ].map(type => (
              <button
                key={type.value}
                type="button"
                onClick={() => {
                  if (type.value === 'ai' && !localStorage.getItem('token')) {
                    setShowLoginPrompt(true);
                    setLoginPromptMessage('Register free to use AI-powered search. AI Search understands natural language and finds patents you might miss with keyword search.');
                  } else {
                    setSearchType(type.value);
                    setQuery('');  // Clear search box when changing search type
                  }
                }}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: searchType === type.value 
                    ? '2px solid #1976d2' 
                    : '2px solid #e0e0e0',
                  background: searchType === type.value ? '#e3f2fd' : 'white',
                  color: searchType === type.value ? '#1976d2' : '#666',
                  cursor: 'pointer',
                  fontWeight: searchType === type.value ? '600' : '400',
                  fontSize: '14px',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {type.icon && <span>{type.icon}</span>}
                {type.label}
                
              </button>
            ))}
          </div>
          
          <p style={{ fontSize: '13px', color: '#10b981', textAlign: 'center', margin: '8px 0 16px 0' }} data-tour="unlimited-message">
            Patent #, Company, and Inventor searches are unlimited and don't require login
          </p>

          {/* Advanced Tools Toggle */}
          <AdvancedToolsToggle 
            showMoreTools={showMoreTools} 
            setShowMoreTools={setShowMoreTools} 
          />

          {/* Expandable Advanced Tools */}
          {showMoreTools && (
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: '8px', 
              justifyContent: 'center',
              marginTop: '12px',
              padding: '16px',
              background: '#fafafa',
              borderRadius: '12px'
            }}>

              {/* TWO PATH OPTIONS */}
              <AdvancedToolsPathOptions
                user={user}
                userUsage={userUsage}
                setPage={setPage}
              />


              {/* ANALYSIS TOOLS SECTION */}
              <AnalysisToolsSection
                user={user}
                setPage={setPage}
                setShowLoginPrompt={setShowLoginPrompt}
                setLoginPromptMessage={setLoginPromptMessage}
              />
            </div>
          )}
        </div>


        <SearchForm
          query={query}
          setQuery={setQuery}
          searchType={searchType}
          loading={loading}
          user={user}
          userUsage={userUsage}
          showSearchTips={showSearchTips}
          setShowSearchTips={setShowSearchTips}
          handleSearch={handleSearch}
        />

        {results && results.length > 0 && (
          <>
            <SearchActionsBar
              setQuery={setQuery}
              setResults={setResults}
              setTotalCount={setTotalCount}
              setTransparency={setTransparency}
              user={user}
              saveSearch={saveSearch}
            />
            
            {/* Landscape Insight Panel - Enhanced */}
            <LandscapeInsightPanel
              landscapeData={landscapeData}
              showLandscapeCharts={showLandscapeCharts}
              setShowLandscapeCharts={setShowLandscapeCharts}
              setPage={setPage}
              user={user}
            />
            
            {landscapeLoading && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', textAlign: 'center' }}>
                Loading landscape insights...
              </div>
            )}

            <ResultsHeader
              totalCount={totalCount}
grantedCount={grantedCount}
  pendingCount={pendingCount}
              results={results}
              query={query}
              user={user}
              patentStatus={patentStatus}
              searchType={searchType}
              setShowLoginPrompt={setShowLoginPrompt}
              setLoginPromptMessage={setLoginPromptMessage}
statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />
            
            <TransparencyPanel
              transparency={transparency}
              showTransparency={showTransparency}
              setShowTransparency={setShowTransparency}
              setQuery={setQuery}
              handleSearch={handleSearch}
              source={source}
            />

{/* Top Patent Holders Panel */}
{topAssignees && topAssignees.length > 0 && (
          <TopAssigneesPanel topAssignees={topAssignees} />
)}

              <ResultsSortBar
                resultsCount={results.length}
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {getFilteredAndSortedResults().map((patent) => (
                <PatentCard
                  key={patent.id}
                  patent={patent}
                  user={user}
                  showEnhancedButtons={searchType === 'assignee'}
                  onViewClaims={handleViewClaims}
                  onViewSummary={handleViewSummary}
                  onViewPCT={handleViewPCT}
                  onViewDescription={handleViewDescription}
                  savedPatents={savedPatents}
                  handleSavePatent={handleSavePatent}
                  savingPatent={savingPatent}
                  citationCounts={citationCounts}
                  expandedCitations={expandedCitations}
                  handleExpandCitations={handleExpandCitations}
                  citationDetails={citationDetails}
                  citationDetailsLoading={citationDetailsLoading}
                  handleTieredAnalysis={handleTieredAnalysis}
                  analysisLoading={analysisLoading}
                  userUsage={userUsage}
                  handleExpandFamily={handleExpandFamily}
                  expandedFamily={expandedFamily}
                  familyLoading={familyLoading}
                  familyData={familyData}
                  setLoginPromptMessage={setLoginPromptMessage}
                  setShowLoginPrompt={setShowLoginPrompt}
                  addToCompare={addToCompare}
                  compareList={compareList}
                  removeFromCompare={removeFromCompare}
                  clearCompare={clearCompare}
                  setShowComparePanel={setShowComparePanel}
                  openShareModal={openShareModal}
                  patentSummaries={patentSummaries}
                  setPatentSummaries={setPatentSummaries}
                />
              ))}
            </div>
          </>
        )}

        <EmptyResultsMessage
          loading={loading}
          hasSearched={hasSearched}
          results={results}
          query={query}
          transparency={transparency}
          onCpcClick={(code) => { setQuery(code); handleSearch(null, code); }}
        />
      </main>
            
      <footer style={{ 
        padding: '32px 16px', 
        textAlign: 'center', 
        borderTop: '1px solid #e0e0e0',
        marginTop: '80px',
        color: '#5e778a',
        fontSize: '14px'
      }}>
        By using Patent Tracker, you agree to our{' '}
        <a href="#" style={{ color: '#53dafc', textDecoration: 'none' }}>Terms</a>
        {' '}and have read our{' '}
        <a href="#" style={{ color: '#53dafc', textDecoration: 'none' }}>Privacy Policy</a>
        . See{' '}
        <a href="#" style={{ color: '#53dafc', textDecoration: 'none' }}>Cookie Preferences</a>.
      </footer>

      {/* Interactive Tour */}
      <Tour
        steps={mainTourSteps}
        isOpen={showTour}
        onClose={() => setShowTour(false)}
        tourKey="main"
      />

        <SidebarMenu menuOpen={menuOpen} setMenuOpen={setMenuOpen} setPage={setPage} user={user} savedSearches={savedSearches} handleSearch={handleSearch} />


        <ComparePanel showComparePanel={showComparePanel} setShowComparePanel={setShowComparePanel} compareList={compareList} savedAiAnalysis={savedAiAnalysis} setSavedAiAnalysis={setSavedAiAnalysis} />

        <CompareTray compareList={compareList} setShowComparePanel={setShowComparePanel} user={user} removeFromCompare={removeFromCompare} clearCompare={clearCompare} />

{/* Login/Upgrade Prompt Modal */}
        <ShareToWorkspaceModal showShareModal={showShareModal} setShowShareModal={setShowShareModal} patentToShare={patentToShare} setPatentToShare={setPatentToShare} userWorkspaces={userWorkspaces} selectedWorkspaceId={selectedWorkspaceId} setSelectedWorkspaceId={setSelectedWorkspaceId} shareNote={shareNote} setShareNote={setShareNote} shareToWorkspace={shareToWorkspace} setPage={setPage} />

        <LoginPromptModal showLoginPrompt={showLoginPrompt} setShowLoginPrompt={setShowLoginPrompt} loginPromptMessage={loginPromptMessage} user={user} setPage={setPage} />
<CompanyDisambiguationModal 
          show={showCompanyDisambiguation}
          onClose={() => {
            setShowCompanyDisambiguation(false);
          }}
          query={pendingCompanySearch}
          matches={companyMatches}
          onSelect={handleCompanySelection}
          loading={false}
        />
</div>
  );
}

export default App;
